/**
 * BehaviorValidation — Static validation of behavior actions against
 * the components currently on the workshop grid.
 * Pure functions, no Phaser dependency.
 */
import { ActionType } from '@/behaviors/BehaviorDefs';
import {
  ComponentType, ComponentCategory,
  COMPONENT_DEFS, type ComponentDef,
} from '@/components/ComponentDefs';

export interface ActionValidation {
  valid: boolean;
  /** i18n key explaining what's missing (only when valid=false) */
  missingReason?: string;
}

/** Movement component types */
const MOVE_TYPES: ReadonlySet<ComponentType> = new Set([
  ComponentType.WHEEL,
  ComponentType.WING,
  ComponentType.TURBINE,
  ComponentType.THRUSTER,
  ComponentType.JUMPER,
]);

/** Actions that require targeting (show target modifier toggle) */
export const TARGET_RELEVANT_ACTIONS: ReadonlySet<ActionType> = new Set([
  ActionType.SHOOT,
  ActionType.CHASE,
  ActionType.PURSUE,
  ActionType.ATTACK_BASE,
  ActionType.PROTECT_ALLY,
  ActionType.REPAIR_ALLY,
  ActionType.RECHARGE_ALLY,
]);

/** Flatten a 2D workshop grid into a list of placed ComponentTypes */
export function getGridComponentTypes(
  grid: (ComponentType | null)[][],
  rows: number,
  cols: number,
): ComponentType[] {
  const types: ComponentType[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const t = grid[r]?.[c];
      if (t != null) types.push(t);
    }
  }
  return types;
}

/** Check if an action is feasible given the components on the grid */
export function validateAction(
  action: ActionType,
  gridComponents: ComponentType[],
): ActionValidation {
  const defs: ComponentDef[] = gridComponents.map(t => COMPONENT_DEFS[t]);

  switch (action) {
    // Weapons (excluding explosive charge)
    case ActionType.SHOOT:
    case ActionType.ATTACK_BASE:
      if (!defs.some(d => d.category === ComponentCategory.WEAPON && d.type !== ComponentType.EXPLOSIVE_CHARGE)) {
        return { valid: false, missingReason: 'behavior.warn.needsWeapon' };
      }
      return { valid: true };

    // Explosive self-destruct
    case ActionType.EXPLODE:
      if (!gridComponents.includes(ComponentType.EXPLOSIVE_CHARGE)) {
        return { valid: false, missingReason: 'behavior.warn.needsExplosive' };
      }
      return { valid: true };

    // Repair support
    case ActionType.REPAIR_ALLY:
    case ActionType.REPAIR_BASE:
      if (!defs.some(d => d.category === ComponentCategory.SUPPORT && d.repairRate > 0)) {
        return { valid: false, missingReason: 'behavior.warn.needsRepair' };
      }
      return { valid: true };

    // Recharge support
    case ActionType.RECHARGE_ALLY:
      if (!defs.some(d => d.category === ComponentCategory.SUPPORT && d.rechargeRate > 0)) {
        return { valid: false, missingReason: 'behavior.warn.needsRecharge' };
      }
      return { valid: true };

    // Movement actions
    case ActionType.ADVANCE:
    case ActionType.RETREAT:
    case ActionType.PATROL:
    case ActionType.CHASE:
    case ActionType.PURSUE:
    case ActionType.FALL_BACK:
    case ActionType.PROTECT_ALLY:
      if (!gridComponents.some(t => MOVE_TYPES.has(t))) {
        return { valid: false, missingReason: 'behavior.warn.needsMovement' };
      }
      return { valid: true };

    // Seek recharger — only meaningful if robot consumes energy
    case ActionType.SEEK_RECHARGER:
      if (!defs.some(d => d.energy < 0)) {
        return { valid: false, missingReason: 'behavior.warn.needsEnergy' };
      }
      return { valid: true };

    // Harvest — needs excavator + movement
    case ActionType.HARVEST:
      if (!gridComponents.includes(ComponentType.EXCAVATOR)) {
        return { valid: false, missingReason: 'behavior.warn.needsExcavator' };
      }
      if (!gridComponents.some(t => MOVE_TYPES.has(t))) {
        return { valid: false, missingReason: 'behavior.warn.needsMovement' };
      }
      return { valid: true };

    // Always valid
    case ActionType.HOLD_POSITION:
    default:
      return { valid: true };
  }
}
